/**
 * Kaçış rules engine (pure, deterministic).
 *
 * A worm is a contiguous path of cells. The player grabs one END (head or tail) and leads it one
 * cell at a time into a free orthogonal cell; the rest of the body follows snake-style. Rules:
 *  - the lead may not enter a wall, a live numbered stone, another worm, or ANY cell of its own body
 *    (so it can never reverse into its neck);
 *  - stepping off the grid is only legal through a gate of the worm's own colour: the worm slides out
 *    and is removed (its letter joins the tray, `escaped` grows, numbered stones with n <= escaped dissolve);
 *  - a gesture (grab → drag → release) that moved >= 1 cell costs one move; exiting ends the gesture.
 */
import { DX, DY, otherEnd, type Cell, type Dir, type End, type Gesture, type KacisLevel } from "./types";

export const FREE = -1;
/** stoneN value of a permanent wall. */
export const WALL = 999;

export type Status = "playing" | "won" | "lost";

export interface WormState {
  id: number;
  color: number;
  letter: string;
  cells: Cell[];
  alive: boolean;
}

export interface ActiveGesture {
  worm: number;
  end: End;
  steps: Dir[];
}

export interface GameState {
  level: KacisLevel;
  cols: number;
  rows: number;
  worms: WormState[];
  /** cell id (y*cols+x) → worm id or FREE. */
  occ: Int16Array;
  /** cell id → 0 none, WALL permanent, k>0 numbered stone (dissolves when escaped >= k). */
  stoneN: Int16Array;
  gateMap: Map<number, number>; // key(x,y,dir) → gate colour
  escaped: number;
  /** Letters of escaped worms, in escape order (the tray). */
  order: string[];
  escapeIds: number[];
  moves: number;
  budget: number;
  status: Status;
  gesture: ActiveGesture | null;
  gestures: Gesture[];
}

export type StepFail = "bounds" | "occupied" | "wall" | "stone" | "self" | "color" | "dead" | "over";
export type StepOutcome =
  | { ok: true; kind: "move"; to: Cell }
  | { ok: true; kind: "exit"; dir: Dir; from: Cell }
  | { ok: false; reason: StepFail; at?: Cell };

export const cellId = (s: { cols: number }, x: number, y: number): number => y * s.cols + x;
export const gateKey = (cols: number, x: number, y: number, dir: number): number => (y * cols + x) * 4 + dir;

export function createGame(level: KacisLevel, budget = level.budget): GameState {
  const { cols, rows } = level;
  const occ = new Int16Array(cols * rows).fill(FREE);
  const stoneN = new Int16Array(cols * rows);
  for (const s of level.stones) stoneN[s.y * cols + s.x] = s.n === 0 ? WALL : s.n;
  const worms: WormState[] = level.worms.map((w) => ({
    id: w.id,
    color: w.color,
    letter: w.letter,
    cells: w.cells.map((c) => ({ x: c.x, y: c.y })),
    alive: true,
  }));
  for (const w of worms) for (const c of w.cells) occ[c.y * cols + c.x] = w.id;
  const gateMap = new Map<number, number>();
  for (const g of level.gates) gateMap.set(gateKey(cols, g.x, g.y, g.dir), g.color);
  return { level, cols, rows, worms, occ, stoneN, gateMap, escaped: 0, order: [], escapeIds: [], moves: 0, budget, status: "playing", gesture: null, gestures: [] };
}

export function cloneState(s: GameState): GameState {
  return {
    ...s,
    worms: s.worms.map((w) => ({ ...w, cells: w.cells.map((c) => ({ x: c.x, y: c.y })) })),
    occ: s.occ.slice(),
    order: s.order.slice(),
    escapeIds: s.escapeIds.slice(),
    gesture: s.gesture ? { ...s.gesture, steps: s.gesture.steps.slice() } : null,
    gestures: s.gestures.slice(),
  };
}

/** Is there something solid (wall / not yet dissolved stone) on this cell? */
export function solidAt(s: GameState, id: number): boolean {
  const v = s.stoneN[id]!;
  return v === WALL || (v > 0 && v > s.escaped);
}

export const leadIndex = (w: WormState, end: End): number => (end === "head" ? 0 : w.cells.length - 1);

/** Can `worm` lead its `end` one step in `dir`? Pure query. */
export function probe(s: GameState, worm: number, end: End, dir: Dir): StepOutcome {
  const w = s.worms[worm];
  if (!w || !w.alive) return { ok: false, reason: "dead" };
  if (s.status !== "playing") return { ok: false, reason: "over" };
  const lead = w.cells[leadIndex(w, end)]!;
  const nx = lead.x + DX[dir];
  const ny = lead.y + DY[dir];
  if (nx < 0 || ny < 0 || nx >= s.cols || ny >= s.rows) {
    const gc = s.gateMap.get(gateKey(s.cols, lead.x, lead.y, dir));
    if (gc === undefined) return { ok: false, reason: "bounds", at: { x: nx, y: ny } };
    if (gc !== w.color) return { ok: false, reason: "color", at: { x: lead.x, y: lead.y } };
    return { ok: true, kind: "exit", dir, from: { x: lead.x, y: lead.y } };
  }
  const id = ny * s.cols + nx;
  const sv = s.stoneN[id]!;
  if (sv === WALL) return { ok: false, reason: "wall", at: { x: nx, y: ny } };
  if (sv > 0 && sv > s.escaped) return { ok: false, reason: "stone", at: { x: nx, y: ny } };
  const o = s.occ[id]!;
  if (o === worm) return { ok: false, reason: "self", at: { x: nx, y: ny } };
  if (o !== FREE) return { ok: false, reason: "occupied", at: { x: nx, y: ny } };
  return { ok: true, kind: "move", to: { x: nx, y: ny } };
}

export interface EscapeInfo {
  worm: number;
  letter: string;
  color: number;
  dissolved: Cell[];
  won: boolean;
  /** Cells the worm occupied when it left (head..tail order as stored). */
  cells: Cell[];
}

export interface StepResult {
  outcome: StepOutcome;
  escape?: EscapeInfo;
  /** The gesture ended with this step (exit). */
  gestureEnded: boolean;
}

/** Start a drag on a worm end. */
export function beginDrag(s: GameState, worm: number, end: End): boolean {
  const w = s.worms[worm];
  if (!w || !w.alive || s.status !== "playing" || s.gesture) return false;
  s.gesture = { worm, end, steps: [] };
  return true;
}

/** Lead the grabbed end one cell. Mutates state when legal. */
export function dragStep(s: GameState, dir: Dir): StepResult {
  const g = s.gesture;
  if (!g) return { outcome: { ok: false, reason: "over" }, gestureEnded: false };
  const out = probe(s, g.worm, g.end, dir);
  if (!out.ok) return { outcome: out, gestureEnded: false };
  const w = s.worms[g.worm]!;
  if (out.kind === "move") {
    const c = out.to;
    if (g.end === "head") {
      const gone = w.cells.pop()!;
      s.occ[gone.y * s.cols + gone.x] = FREE;
      w.cells.unshift(c);
    } else {
      const gone = w.cells.shift()!;
      s.occ[gone.y * s.cols + gone.x] = FREE;
      w.cells.push(c);
    }
    s.occ[c.y * s.cols + c.x] = w.id;
    g.steps.push(dir);
    return { outcome: out, gestureEnded: false };
  }
  // exit through the gate
  const cells = w.cells.map((c) => ({ x: c.x, y: c.y }));
  for (const c of w.cells) s.occ[c.y * s.cols + c.x] = FREE;
  w.alive = false;
  s.escaped++;
  s.order.push(w.letter);
  s.escapeIds.push(w.id);
  g.steps.push(dir);
  const dissolved: Cell[] = [];
  for (let i = 0; i < s.stoneN.length; i++) {
    const v = s.stoneN[i]!;
    if (v > 0 && v < WALL && v === s.escaped) dissolved.push({ x: i % s.cols, y: Math.floor(i / s.cols) });
  }
  s.gestures.push({ worm: g.worm, end: g.end, steps: g.steps.slice() });
  s.gesture = null;
  s.moves++;
  const won = s.worms.every((x) => !x.alive);
  if (won) s.status = "won";
  else if (s.moves >= s.budget) s.status = "lost";
  return { outcome: out, escape: { worm: w.id, letter: w.letter, color: w.color, dissolved, won, cells }, gestureEnded: true };
}

/** Release the grab. A gesture that moved >= 1 cell costs one move. */
export function endDrag(s: GameState): { moved: boolean } {
  const g = s.gesture;
  s.gesture = null;
  if (!g || g.steps.length === 0) return { moved: false };
  s.gestures.push({ worm: g.worm, end: g.end, steps: g.steps.slice() });
  s.moves++;
  if (s.status === "playing" && s.moves >= s.budget) s.status = "lost";
  return { moved: true };
}

/** Apply a whole gesture; stops at the first illegal step. */
export function applyGesture(s: GameState, g: Gesture): { ok: boolean; applied: number } {
  if (!beginDrag(s, g.worm, g.end)) return { ok: false, applied: 0 };
  let applied = 0;
  for (const d of g.steps) {
    const r = dragStep(s, d);
    if (!r.outcome.ok) break;
    applied++;
    if (r.gestureEnded) return { ok: applied === g.steps.length, applied };
  }
  endDrag(s);
  return { ok: applied === g.steps.length, applied };
}

/** Replay a list of gestures from the start. Illegal steps are ignored by the engine (state just stops there). */
export function replay(level: KacisLevel, gestures: Gesture[], budget = level.budget): GameState {
  const s = createGame(level, budget);
  for (const g of gestures) {
    if (s.status !== "playing") break;
    applyGesture(s, g);
  }
  return s;
}

/** ★ Kelime: the tray spells the hidden word. */
export function wordStar(s: GameState): boolean {
  return s.status === "won" && s.order.join("") === s.level.word;
}

/** H4: a level that failed 3 times can be skipped for free (never sold). */
export const SKIP_AFTER_FAILS = 3;
export function canSkip(fails: number): boolean {
  return fails >= SKIP_AFTER_FAILS;
}

export const movesLeft = (s: GameState): number => Math.max(0, s.budget - s.moves);

/** Map stars: the ★ word order earns three; otherwise a fifth of the moves to spare earns two. */
export function starsFor(s: GameState): 1 | 2 | 3 {
  if (wordStar(s)) return 3;
  return movesLeft(s) >= Math.max(1, Math.ceil(s.budget * 0.2)) ? 2 : 1;
}

/** Which cells of the live state are blocked for `worm` (everything but free cells and its own). Debug/test helper. */
export function freeFor(s: GameState, worm: number, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= s.cols || y >= s.rows) return false;
  const id = y * s.cols + x;
  if (solidAt(s, id)) return false;
  return s.occ[id] === FREE || s.occ[id] === worm;
}

/**
 * Inverse of a gesture that was just applied (state `before` → gesture → state after): the same worm,
 * the opposite end leading, retracing the vacated cells. Returns null when the gesture exited.
 */
export function invertGesture(before: GameState, g: Gesture): Gesture | null {
  const w = before.worms[g.worm]!;
  const L = w.cells.length;
  // Trail the worm window slides along: old cells from the trailing end toward the lead, then new cells.
  const old = g.end === "head" ? w.cells.slice().reverse() : w.cells.slice();
  const trail: Cell[] = old.map((c) => ({ x: c.x, y: c.y }));
  let cur = g.end === "head" ? w.cells[0]! : w.cells[L - 1]!;
  for (const d of g.steps) {
    cur = { x: cur.x + DX[d], y: cur.y + DY[d] };
    if (cur.x < 0 || cur.y < 0 || cur.x >= before.cols || cur.y >= before.rows) return null;
    trail.push(cur);
  }
  const m = g.steps.length;
  const steps: Dir[] = [];
  for (let i = m; i > 0; i--) {
    const a = trail[i]!;
    const b = trail[i - 1]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const d = (dx === 1 ? 1 : dx === -1 ? 3 : dy === 1 ? 2 : 0) as Dir;
    if (Math.abs(dx) + Math.abs(dy) !== 1) return null;
    steps.push(d);
  }
  return { worm: g.worm, end: otherEnd(g.end), steps };
}
